import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Plus, User, ShieldCheck, EyeSlash, Eye, Star, Key, Trash,
  ArrowsClockwise, Copy, SignOut, EnvelopeSimple, CheckCircle,
} from "@phosphor-icons/react";
import ActivationLinkModal from "./ActivationLinkModal";
import { getAppBaseUrl } from "../lib/appUrls";

/**
 * Customer-portal + admin surface for `customer_users` management.
 *
 * `api`      — an axios-like client that already carries auth.
 * `basePath` — either "/customer-portal/user-access" (customer surface)
 *              or "/admin/portal-users" (admin surface).
 * `addPath`  — where to POST a new user:
 *              customer_admin  → "/customer-portal/user-access"
 *              staff/admin     → "/admin/customers/{customer_id}/portal-users"
 * `canManage` — whether the current caller can invoke mutation actions.
 * `readOnly` — extra hint used to hide all action buttons for viewers.
 * `title` / `subtitle` — page chrome.
 */
export default function UserAccessTable({
  api, basePath, addPath, canManage, readOnly, title, subtitle,
  users, refresh, testIdPrefix = "portal-users",
  showAdminSecurityControls = false,   // Phase B — admin surface only
}) {
  const [showAdd, setShowAdd] = useState(false);
  const [showRoleFor, setShowRoleFor] = useState(null);
  const [activationToken, setActivationToken] = useState(null);
  const [activationEmail, setActivationEmail] = useState("");
  const [busy, setBusy] = useState(null);
  const [activeUserId, setActiveUserId] = useState(null);
  const [activationLinkKind, setActivationLinkKind] = useState("activation");

  const primaryId = useMemo(
    () => (users || []).find(u => u.is_primary)?.user_id || null,
    [users],
  );

  const handleAction = async (endpoint, opts = {}) => {
    setBusy(endpoint);
    try {
      const { data } = await api.post(endpoint, opts.body || {});
      if (opts.after) opts.after(data);
      if (opts.silent !== true) toast.success(opts.successMsg || "Done");
      refresh?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Action failed";
      toast.error(msg);
    } finally {
      setBusy(null);
    }
  };

  const handleDelete = async (endpoint) => {
    setBusy(endpoint);
    try {
      await api.delete(endpoint);
      toast.success("Access removed");
      refresh?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Remove failed";
      toast.error(msg);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}-page`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">{title}</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-xl">{subtitle}</p>
        </div>
        {canManage && !readOnly && (
          <button
            onClick={() => setShowAdd(true)}
            data-testid={`${testIdPrefix}-add-btn`}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
          >
            <Plus size={16} weight="bold" /> Add User
          </button>
        )}
      </div>

      {/* table */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm" data-testid={`${testIdPrefix}-table`}>
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-3">Full Name</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Primary</th>
              <th className="text-left px-4 py-3">Login Method</th>
              <th className="text-left px-4 py-3">Last Login</th>
              <th className="text-left px-4 py-3">Added By</th>
              <th className="text-left px-4 py-3">Added Date</th>
              {showAdminSecurityControls && <th className="text-left px-4 py-3">Security</th>}
              {canManage && !readOnly && <th className="text-right px-4 py-3">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {(users || []).length === 0 && (
              <tr>
                <td
                  colSpan={canManage && !readOnly ? 10 : 9}
                  className="text-center py-8 text-slate-500"
                  data-testid={`${testIdPrefix}-empty`}
                >
                  No portal users yet.
                </td>
              </tr>
            )}
            {(users || []).map((u) => (
              <tr key={u.user_id} className="border-t border-slate-100" data-testid={`${testIdPrefix}-row-${u.email_normalized}`}>
                <td className="px-4 py-3 text-slate-800">
                  {[u.first_name, u.last_name].filter(Boolean).join(" ") || <span className="text-slate-400">—</span>}
                </td>
                <td className="px-4 py-3 font-mono text-xs text-slate-800">{u.email}</td>
                <td className="px-4 py-3">
                  <RoleBadge role={u.role} />
                </td>
                <td className="px-4 py-3">
                  <StatusBadge status={u.status} />
                </td>
                <td className="px-4 py-3">
                  {u.is_primary
                    ? <span className="inline-flex items-center gap-1 text-amber-600" data-testid={`${testIdPrefix}-primary-mark`}><Star weight="fill" size={14} /> Primary</span>
                    : <span className="text-slate-400">—</span>}
                </td>
                <td className="px-4 py-3 text-slate-600 capitalize">{u.password_configured ? (u.google_linked ? "Google + Password" : "Password") : (u.login_method || "google")}</td>
                <td className="px-4 py-3 text-slate-600 text-xs font-mono">
                  {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : <span className="text-slate-400">Never</span>}
                </td>
                <td className="px-4 py-3 text-slate-600 text-xs">{u.added_by_email || u.auto_created_source || <span className="text-slate-400">—</span>}</td>
                <td className="px-4 py-3 text-slate-600 text-xs font-mono">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                </td>
                {showAdminSecurityControls && (
                  <td className="px-4 py-3 text-xs">
                    <div className="space-y-0.5">
                      <div><span className="text-slate-500">Password:</span>{" "}
                        <span className={u.password_configured ? "text-emerald-700 font-medium" : "text-slate-500"}>{u.password_configured ? "Yes" : "No"}</span></div>
                      <div><span className="text-slate-500">Google:</span>{" "}
                        <span className={u.google_linked ? "text-emerald-700 font-medium" : "text-slate-500"}>{u.google_linked ? "Yes" : "No"}</span></div>
                      {u.password_updated_at && (
                        <div className="text-slate-500 font-mono">PW: {new Date(u.password_updated_at).toLocaleDateString()}</div>
                      )}
                      {u.last_successful_login_at && (
                        <div className="text-slate-500 font-mono">Login: {new Date(u.last_successful_login_at).toLocaleDateString()}</div>
                      )}
                      {u.lockout_until && new Date(u.lockout_until) > new Date() && (
                        <div className="text-rose-700 font-medium">Locked</div>
                      )}
                    </div>
                  </td>
                )}
                {canManage && !readOnly && (
                  <td className="px-4 py-3 text-right">
                    <RowActions
                      u={u}
                      busy={busy}
                      isPrimary={u.user_id === primaryId}
                      onEditRole={() => setShowRoleFor(u)}
                      onSetPrimary={() =>
                        handleAction(`${basePath}/${u.user_id}/set-primary`, { successMsg: "Primary user updated" })
                      }
                      onDisable={() =>
                        handleAction(`${basePath}/${u.user_id}/disable`, { successMsg: "Access disabled" })
                      }
                      onReenable={() =>
                        handleAction(`${basePath}/${u.user_id}/reenable`, { successMsg: "Access re-enabled" })
                      }
                      onRemove={() => {
                        if (!window.confirm(`Remove ${u.email}? They will lose portal access immediately and all sessions will be revoked.`)) return;
                        handleDelete(`${basePath}/${u.user_id}`);
                      }}
                      onRevokeSessions={() =>
                        handleAction(`${basePath}/${u.user_id}/revoke-sessions`, { successMsg: "Sessions revoked" })
                      }
                      onRegenerate={() =>
                        handleAction(`${basePath}/${u.user_id}/regenerate-activation`, {
                          successMsg: "Activation link regenerated",
                          after: (data) => {
                            setActiveUserId(u.user_id);
                            setActivationEmail(u.email);
                            setActivationLinkKind("activation");
                            setActivationToken(data.activation_token);
                          },
                        })
                      }
                      onCopyExisting={u.status === "invited" ? undefined : null}
                      showAdminSecurityControls={showAdminSecurityControls}
                      onGenerateResetLink={showAdminSecurityControls ? () =>
                        handleAction(`/admin/portal-users/${u.user_id}/reset-link`, {
                          successMsg: "Reset link generated",
                          after: (data) => {
                            setActiveUserId(u.user_id);
                            setActivationEmail(u.email);
                            setActivationLinkKind("reset");
                            setActivationToken(data.reset_token);
                          },
                        })
                      : null}
                      onSendActivation={showAdminSecurityControls ? () => {
                        if (!window.confirm(`Send ONE activation email to ${u.email}? A fresh 7-day single-use link will be generated.`)) return;
                        handleAction(`/admin/portal-users/${u.user_id}/send-activation-email`, {
                          silent: true,
                          after: (data) => toast.success(data.message || "Activation email sent"),
                        });
                      } : null}
                      onClearLockout={showAdminSecurityControls ? () =>
                        handleAction(`/admin/portal-users/${u.user_id}/clear-lockout`, {
                          successMsg: "Lockout cleared",
                        })
                      : null}
                    />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <AddUserModal
          onClose={() => setShowAdd(false)}
          onSubmit={async (payload) => {
            try {
              const { data } = await api.post(addPath, payload);
              toast.success("User added");
              setActiveUserId(data.user.user_id);
              setActivationEmail(data.user.email);
              setActivationLinkKind("activation");
              setActivationToken(data.activation_token);
              setShowAdd(false);
              refresh?.();
            } catch (e) {
              const msg = e?.response?.data?.detail || e?.message || "Could not add user";
              toast.error(msg);
            }
          }}
          testIdPrefix={testIdPrefix}
        />
      )}
      {showRoleFor && (
        <EditRoleModal
          user={showRoleFor}
          onClose={() => setShowRoleFor(null)}
          onSubmit={async (role) => {
            try {
              await api.patch(`${basePath}/${showRoleFor.user_id}`, { role });
              toast.success("Role updated");
              setShowRoleFor(null);
              refresh?.();
            } catch (e) {
              const msg = e?.response?.data?.detail || e?.message || "Could not update role";
              toast.error(msg);
            }
          }}
        />
      )}
      <ActivationLinkModal
        open={!!activationToken}
        token={activationToken}
        email={activationEmail}
        linkKind={activationLinkKind}
        onClose={() => setActivationToken(null)}
        onRegenerate={activeUserId ? async () => {
          try {
            const { data } = await api.post(`${basePath}/${activeUserId}/regenerate-activation`);
            setActivationToken(data.activation_token);
            toast.success("Activation link regenerated");
            refresh?.();
          } catch (e) {
            const msg = e?.response?.data?.detail || "Could not regenerate";
            toast.error(msg);
          }
        } : undefined}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------

function RoleBadge({ role }) {
  const map = {
    customer_admin:  ["bg-indigo-50 text-indigo-700 border-indigo-200", "Admin"],
    customer_viewer: ["bg-slate-50 text-slate-700 border-slate-200",  "Viewer"],
  };
  const [cls, label] = map[role] || ["bg-slate-50 text-slate-600 border-slate-200", role || "—"];
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${cls}`}>{label}</span>;
}

function StatusBadge({ status }) {
  const map = {
    active:   ["bg-emerald-50 text-emerald-700 border-emerald-200", "Activated"],
    invited:  ["bg-amber-50 text-amber-700 border-amber-200",       "Not activated"],
    disabled: ["bg-orange-50 text-orange-700 border-orange-200",    "Disabled"],
    removed:  ["bg-rose-50 text-rose-700 border-rose-200",          "Removed"],
  };
  const [cls, label] = map[status] || ["bg-slate-50 text-slate-600 border-slate-200", status];
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${cls}`} data-testid={`portal-user-status-${status}`}>{label}</span>;
}

function RowActions({ u, busy, isPrimary, onEditRole, onSetPrimary, onDisable, onReenable, onRemove, onRegenerate, onRevokeSessions, showAdminSecurityControls, onGenerateResetLink, onClearLockout, onSendActivation }) {
  const activated = u.status === "active" || u.password_configured || !!u.activation_used_at;
  const b = (label, Icon, fn, cls = "", testid, disabled) => (
    <button
      onClick={fn}
      disabled={busy || disabled}
      title={label}
      aria-label={label}
      data-testid={testid}
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-xs hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed ${cls}`}
    >
      <Icon size={13} weight="bold" /> <span className="hidden xl:inline">{label}</span>
    </button>
  );
  return (
    <div className="flex flex-wrap justify-end gap-1.5">
      {b("Edit Role", ShieldCheck, onEditRole, "border-slate-200 text-slate-700", `portal-user-edit-${u.email_normalized}`)}
      {b("Set Primary", Star, onSetPrimary, "border-amber-200 text-amber-700", `portal-user-primary-${u.email_normalized}`, isPrimary || u.status !== "active")}
      {b("Regen Link", ArrowsClockwise, onRegenerate, "border-indigo-200 text-indigo-700", `portal-user-regen-${u.email_normalized}`, u.status === "removed")}
      {u.status === "disabled"
        ? b("Re-enable", Eye, onReenable, "border-emerald-200 text-emerald-700", `portal-user-reenable-${u.email_normalized}`)
        : b("Disable", EyeSlash, onDisable, "border-orange-200 text-orange-700", `portal-user-disable-${u.email_normalized}`, u.status === "removed")}
      {b("Revoke Sessions", SignOut, onRevokeSessions, "border-slate-200 text-slate-700", `portal-user-revoke-${u.email_normalized}`)}
      {showAdminSecurityControls && onGenerateResetLink &&
        b("Reset Link", Key, onGenerateResetLink, "border-emerald-200 text-emerald-700", `portal-user-reset-link-${u.email_normalized}`, u.status === "removed")}
      {showAdminSecurityControls && onSendActivation && activated && (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-emerald-200 text-emerald-700 text-xs" data-testid={`portal-user-already-activated-${u.email_normalized}`}>
          <CheckCircle size={13} weight="bold" /> Already Activated
        </span>
      )}
      {showAdminSecurityControls && onSendActivation && !activated && u.status === "invited" &&
        b("Send Activation Email", EnvelopeSimple, onSendActivation, "border-sky-200 text-sky-700", `portal-user-send-activation-${u.email_normalized}`)}
      {showAdminSecurityControls && onClearLockout &&
        b("Clear Lockout", ShieldCheck, onClearLockout, "border-slate-200 text-slate-700", `portal-user-clear-lockout-${u.email_normalized}`)}
      {b("Remove", Trash, onRemove, "border-rose-200 text-rose-700", `portal-user-remove-${u.email_normalized}`, u.status === "removed")}
    </div>
  );
}

function AddUserModal({ onClose, onSubmit, testIdPrefix }) {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", role: "customer_viewer",
    notes: "", is_primary: false,
  });
  const [submitting, setSubmitting] = useState(false);
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm" data-testid={`${testIdPrefix}-add-modal`}>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setSubmitting(true);
          try { await onSubmit(form); } finally { setSubmitting(false); }
        }}
        className="w-full max-w-md rounded-2xl bg-white shadow-2xl border border-slate-200"
      >
        <div className="px-5 py-4 border-b border-slate-100">
          <div className="text-base font-semibold text-slate-900">Add customer portal user</div>
          <div className="text-xs text-slate-500 mt-1">
            They'll receive an activation link (single-use, 7-day expiry). No email is sent automatically —
            copy the link on the next screen.
          </div>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Input label="First name" value={form.first_name} onChange={(v) => setForm(f => ({ ...f, first_name: v }))} testid={`${testIdPrefix}-add-first`} />
            <Input label="Last name"  value={form.last_name}  onChange={(v) => setForm(f => ({ ...f, last_name: v }))}  testid={`${testIdPrefix}-add-last`} />
          </div>
          <Input required type="email" label="Email"
                 value={form.email}
                 onChange={(v) => setForm(f => ({ ...f, email: v }))}
                 testid={`${testIdPrefix}-add-email`} />
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Role</label>
            <select
              value={form.role}
              onChange={(e) => setForm(f => ({ ...f, role: e.target.value }))}
              className="w-full rounded-lg border border-slate-300 text-sm px-3 py-2"
              data-testid={`${testIdPrefix}-add-role`}
            >
              <option value="customer_viewer">Viewer — read-only access</option>
              <option value="customer_admin">Admin — full portal access + manage users</option>
            </select>
          </div>
          <Input label="Notes (optional)" value={form.notes} onChange={(v) => setForm(f => ({ ...f, notes: v }))} testid={`${testIdPrefix}-add-notes`} />
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.is_primary}
                   onChange={(e) => setForm(f => ({ ...f, is_primary: e.target.checked }))}
                   data-testid={`${testIdPrefix}-add-primary`} />
            Set as Primary user
          </label>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2">
          <button type="button" onClick={onClose}
                  className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-100">
            Cancel
          </button>
          <button type="submit" disabled={submitting}
                  data-testid={`${testIdPrefix}-add-submit`}
                  className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-800 disabled:opacity-50">
            {submitting ? "Adding…" : "Add user"}
          </button>
        </div>
      </form>
    </div>
  );
}

function EditRoleModal({ user, onClose, onSubmit }) {
  const [role, setRole] = useState(user.role || "customer_viewer");
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm" data-testid="portal-users-role-modal">
      <div className="w-full max-w-sm rounded-2xl bg-white shadow-2xl border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-100">
          <div className="text-base font-semibold text-slate-900">Edit role</div>
          <div className="text-xs text-slate-500 mt-1 font-mono">{user.email}</div>
        </div>
        <div className="px-5 py-4">
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="w-full rounded-lg border border-slate-300 text-sm px-3 py-2"
            data-testid="portal-users-role-select"
          >
            <option value="customer_viewer">Viewer</option>
            <option value="customer_admin">Admin</option>
          </select>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-100">Cancel</button>
          <button onClick={() => onSubmit(role)}
                  data-testid="portal-users-role-save"
                  className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-800">Save</button>
        </div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="text-xs font-medium text-slate-600 block mb-1">{label}</label>
      <input
        type={type}
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full rounded-lg border border-slate-300 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-400"
      />
    </div>
  );
}
